import { CardApiTagTypes, CardApiUrls } from "@/modules/card/common/enum";
import type {
  CardOnlineTransactionsResponse,
  ICardItemRequest,
  ICardRequest,
  ICardsRequest,
  ICardTransactionsRequest,
  IRenameCardRequest,
  TCardResponse,
  TCardsResponse,
  TCardTransactionsResponse,
} from "@/modules/card/common/types";
import { ResponseType } from "@/shared/common/enums";
import type {
  IBlobError,
  IBlobUrlResponse,
  ICardHolder,
  IStatementWithIdDownloadRequest,
  IStatementWithIdRequest,
} from "@/shared/common/types";
import { buildRoute, generateQuery } from "@/shared/helpers";
import { blobToText } from "@/shared/utils/common";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "app/axios";

export const cardApi = createApi({
  reducerPath: "cardApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: [CardApiTagTypes.CardList],
  endpoints: (builder) => ({
    getCards: builder.query<TCardsResponse, ICardsRequest>({
      query: ({ pageIndex, pageSize }) => {
        const query = generateQuery({
          pageIndex,
          pageSize,
        });
        const url = `${CardApiUrls.Cards}?${query}`;
        return { url };
      },
      providesTags: [CardApiTagTypes.CardList],
    }),
    getCardById: builder.query<TCardResponse, ICardRequest>({
      query: ({ cardId }) => ({
        url: `${CardApiUrls.Cards}/${cardId}`
      })
    }),
    getCardBalance: builder.query<number, ICardItemRequest>({
      query: ({ cardId }) => {
        const url = buildRoute(CardApiUrls.CardBalance, { cardId });
        return { url };
      },
    }),
    getCardStatus: builder.query<string, ICardItemRequest>({
      query: ({ cardId }) => {
        const url = buildRoute(CardApiUrls.CardStatus, { cardId });
        return { url };
      },
    }),
    getCardHolder: builder.query<ICardHolder, string>({
      query: (cardNumber) => {
        const url = buildRoute(CardApiUrls.CardHolder, { cardNumber });
        return { url };
      },
    }),
    getCardTransactions: builder.query<
      TCardTransactionsResponse,
      ICardTransactionsRequest>({
      query: ({
        cardId,
        pageIndex,
        pageSize,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          pageIndex,
          pageSize,
          startDate,
          endDate,
        });
        const url = buildRoute(CardApiUrls.CardTransactions, { cardId });
        return { url: `${url}?${query}`, };
      }
    }),
    getCardOnlineTransactions: builder.query<
      CardOnlineTransactionsResponse,
      ICardTransactionsRequest>({
      query: ({
        cardId,
        pageIndex,
        pageSize,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          pageIndex,
          pageSize,
          startDate,
          endDate,
        });
        const url = buildRoute(CardApiUrls.CardOnlineTransactions, { cardId });
        return {
          url: `${url}?${query}`,
        };
      }
    }),
    renameCard: builder.mutation<void, IRenameCardRequest>({
      query: ({ cardId, data }) => ({
        url: buildRoute(CardApiUrls.CardRename, { cardId }),
        method: "PATCH",
        data: data.name,
      })
    }),
    getCardStatement: builder.query<string, IStatementWithIdRequest>({
      query: ({
        id,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          startDate,
          endDate,
        });
        const url = buildRoute(CardApiUrls.CardStatement, { cardId: id });
        return {
          url: `${url}?${query}`,
        };
      }
    }),
    downloadCardStatement: builder.query<
      IBlobUrlResponse,
      IStatementWithIdDownloadRequest
    >({
      query: ({
        id,
        exportFormat,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          exportFormat,
          startDate,
          endDate,
        });
        const url = buildRoute(CardApiUrls.CardStatementExport, { cardId: id });
        return {
          url: `${url}?${query}`,
          responseType: ResponseType.Blob
        };
      },
      transformResponse: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        return { url };
      },
      transformErrorResponse: async (error: IBlobError) => {
        const text = await blobToText(error) as string;
        const data = text ? JSON.parse(text) : null;

        return {
          status: error.status,
          data
        };
      }
    }),
  }),
});

export const {
  useGetCardsQuery,
  useGetCardByIdQuery,
  useGetCardStatusQuery,
  useGetCardTransactionsQuery,
  useGetCardOnlineTransactionsQuery,
  useRenameCardMutation,
  useGetCardStatementQuery,
  useLazyGetCardBalanceQuery,
  useLazyGetCardHolderQuery,
  useLazyDownloadCardStatementQuery,
} = cardApi;
